import { Browser } from '@capacitor/browser';
import { motion, AnimatePresence } from 'framer-motion';
import { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import type { ExerciseInfoCard as ExerciseInfoCardType } from '../../lib/workoutCards';
import type { MovementPattern, ExerciseEquipment } from '../../types';
import { isNativeShell } from '../../core/capacitor';
import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';

const MOVEMENT_PATTERN_LABELS: Record<MovementPattern, string> = {
  squat: 'Squat',
  hinge: 'Hinge',
  lunge: 'Lunge',
  push_horizontal: 'Push (Horizontal)',
  push_vertical: 'Push (Vertical)',
  pull_horizontal: 'Pull (Horizontal)',
  pull_vertical: 'Pull (Vertical)',
  core: 'Core',
  carry: 'Carry',
  conditioning: 'Conditioning',
  warmup: 'Warmup',
};

const EQUIPMENT_LABELS: Record<ExerciseEquipment, string> = {
  bodyweight: 'Bodyweight',
  dumbbell: 'Dumbbell',
  barbell: 'Barbell',
  kettlebell: 'Kettlebell',
  cable: 'Cable',
  machine: 'Machine',
  band: 'Band',
};

interface ExerciseInfoCardProps {
  card: ExerciseInfoCardType;
  onSkip?: (reason?: string) => void;
  onSwap?: () => void;
}

const SKIP_REASONS = [
  'Equipment unavailable',
  'Injury/discomfort',
  'Short on time',
  'Already did this today',
];

export function ExerciseInfoCard({ card, onSkip, onSwap }: ExerciseInfoCardProps) {
  const [showSkipConfirm, setShowSkipConfirm] = useState(false);
  const [skipReason, setSkipReason] = useState('');
  const [customReason, setCustomReason] = useState('');

  const handleYouTubeSearch = async () => {
    const query = encodeURIComponent(`${card.exerciseName} exercise`);
    const url = `https://www.youtube.com/results?search_query=${query}`;
    if (isNativeShell()) {
      await Browser.open({ url, presentationStyle: 'popover' });
    } else {
      window.open(url, '_blank', 'noopener');
    }
  };

  const handleSkipConfirm = () => {
    const reason = skipReason === 'custom' ? customReason.trim() : skipReason;
    onSkip?.(reason || undefined);
    setShowSkipConfirm(false);
    setSkipReason('');
    setCustomReason('');
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.3 }}
        className="h-full"
      >
        <Card padding="lg" variant="elevated" className="h-full flex flex-col justify-center space-y-6">
          <div className="flex flex-wrap items-center justify-end gap-2">
            <button
              onClick={handleYouTubeSearch}
              className="inline-flex min-h-10 items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-700 transition hover:border-red-300 hover:bg-red-100 dark:border-red-400/25 dark:bg-red-500/10 dark:text-red-200 dark:hover:bg-red-500/15"
              title="Search YouTube for demo"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
              </svg>
              <span>Find demo</span>
            </button>
            {onSwap && (
                <Button
                  variant={card.isSwapped ? 'secondary' : 'outline'}
                  size="sm"
                  onClick={onSwap}
                  className="backdrop-blur-xl"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7h11m0 0-3-3m3 3-3 3M20 17H9m0 0 3-3m-3 3 3 3" />
                  </svg>
                  Swap
                </Button>
              )}
              {onSkip && (
                <button
                  onClick={() => setShowSkipConfirm(true)}
                  className="p-2 rounded-lg text-gray-400 hover:text-gray-600 dark:text-slate-500 dark:hover:text-slate-300 hover:bg-gray-100 dark:hover:bg-white/10 transition"
                  title="Skip this exercise"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                  </svg>
                </button>
              )}
          </div>

          <div className="text-center space-y-4">
            <Badge variant="info" size="md">Exercise</Badge>

            <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
              {card.exerciseName}
            </h2>

            {card.isSwapped && (
              <div className="flex justify-center">
                <Badge variant="warning" size="sm">
                  Swapped for this session
                </Badge>
              </div>
            )}

            {/* Show howTo (step-by-step instructions) if available */}
            {card.howTo && (
              <div className="text-left bg-gray-50 dark:bg-white/5 rounded-xl p-4 max-w-2xl mx-auto">
                <p className="text-xs font-semibold text-gray-500 dark:text-slate-500 uppercase tracking-wide mb-2">
                  How To
                </p>
                <div className="text-sm text-gray-700 dark:text-slate-300 prose prose-sm dark:prose-invert max-w-none">
                  <ReactMarkdown>{card.howTo}</ReactMarkdown>
                </div>
              </div>
            )}

            {/* Movement Pattern & Equipment Tags */}
            {(card.movementPattern || (card.equipmentTags && card.equipmentTags.length > 0)) && (
              <div className="flex flex-wrap gap-2 justify-center items-center">
                {card.movementPattern && (
                  <Badge variant="primary" size="sm">
                    {MOVEMENT_PATTERN_LABELS[card.movementPattern]}
                  </Badge>
                )}
                {card.equipmentTags && card.equipmentTags.map((equipment) => (
                  <Badge key={equipment} variant="default" size="sm">
                    {EQUIPMENT_LABELS[equipment]}
                  </Badge>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-center gap-6 flex-wrap">
            <div className="text-center">
              <p className="text-3xl font-bold text-primary-600 dark:text-primary-400">
                {card.setsPlanned}
              </p>
              <p className="text-sm text-gray-600 dark:text-slate-400 uppercase tracking-wide">
                Sets
              </p>
            </div>

            <div className="text-center">
              <p className="text-3xl font-bold text-primary-600 dark:text-primary-400">
                {card.repsPlanned}
              </p>
              <p className="text-sm text-gray-600 dark:text-slate-400 uppercase tracking-wide">
                Reps
              </p>
            </div>

            <div className="text-center">
              <p className="text-3xl font-bold text-primary-600 dark:text-primary-400">
                {card.restSecPlanned}s
              </p>
              <p className="text-sm text-gray-600 dark:text-slate-400 uppercase tracking-wide">
                Rest
              </p>
            </div>
          </div>
        </Card>
      </motion.div>

      {/* Skip Confirmation Modal */}
      <AnimatePresence>
        {showSkipConfirm && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowSkipConfirm(false)}
              className="fixed inset-0 bg-black/50 z-50"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="fixed inset-x-4 top-1/2 -translate-y-1/2 max-w-sm mx-auto bg-white dark:bg-slate-800 rounded-2xl shadow-2xl z-50 p-6"
            >
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                Skip {card.exerciseName}?
              </h3>
              <p className="text-sm text-gray-600 dark:text-slate-400 mb-4">
                You can always undo this from the playlist.
              </p>

              {/* Reason selection */}
              <div className="space-y-2 mb-4">
                <p className="text-xs font-medium text-gray-500 dark:text-slate-500 uppercase tracking-wide">
                  Reason (optional)
                </p>
                <div className="flex flex-wrap gap-2">
                  {SKIP_REASONS.map((reason) => (
                    <button
                      key={reason}
                      onClick={() => setSkipReason(skipReason === reason ? '' : reason)}
                      className={`px-3 py-1.5 text-sm rounded-full transition ${
                        skipReason === reason
                          ? 'bg-primary-500 text-white'
                          : 'bg-gray-100 dark:bg-white/10 text-gray-700 dark:text-slate-300 hover:bg-gray-200 dark:hover:bg-white/20'
                      }`}
                    >
                      {reason}
                    </button>
                  ))}
                  <button
                    onClick={() => setSkipReason(skipReason === 'custom' ? '' : 'custom')}
                    className={`px-3 py-1.5 text-sm rounded-full transition ${
                      skipReason === 'custom'
                        ? 'bg-primary-500 text-white'
                        : 'bg-gray-100 dark:bg-white/10 text-gray-700 dark:text-slate-300 hover:bg-gray-200 dark:hover:bg-white/20'
                    }`}
                  >
                    Other...
                  </button>
                </div>
                {skipReason === 'custom' && (
                  <input
                    type="text"
                    value={customReason}
                    onChange={(e) => setCustomReason(e.target.value)}
                    placeholder="Enter reason..."
                    className="w-full mt-2 px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-white/10 bg-white dark:bg-slate-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    autoFocus
                  />
                )}
              </div>

              <div className="flex gap-3">
                <Button
                  variant="ghost"
                  className="flex-1"
                  onClick={() => {
                    setShowSkipConfirm(false);
                    setSkipReason('');
                    setCustomReason('');
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  className="flex-1"
                  onClick={handleSkipConfirm}
                >
                  Skip Exercise
                </Button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
